import { useMemo, type CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import s from "./Inspections.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useInspections } from "../../api/queries";
import {
  INSPECTION_STATUS_LABEL,
  INSPECTION_STATUSES,
  inspectionWhen,
  sortInspections,
  summariseInspections,
  type Inspection,
  type InspectionStatus,
} from "../../domain/inspections";
import { EmptyState, ErrorState, numClass, Select, Skeleton, Table, tableRowLinkClass } from "../../design/components";
import { formatDateTime } from "../../lib/format";
import { t, tn } from "../../i18n";

export function InspectionsPage() {
  const { site } = useSession();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const status = (params.get("status") as InspectionStatus | null) || null;
  const template = params.get("template") || null;

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  /* Status and template are the platform's own filters; both go to it. */
  const rounds = useInspections({ status: status ?? undefined, template: template ?? undefined });
  const all = useInspections();
  const rows = useMemo(() => sortInspections(rounds.data ?? []), [rounds.data]);
  const summary = useMemo(() => summariseInspections(all.data ?? []), [all.data]);
  const templates = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of all.data ?? []) m.set(i.template, i.templateName);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [all.data]);

  return (
    <Page eyebrow={t("{code} · inspections", { code: site.code })} title="Inspections">
      <div className={s.summary}>
        <Stat label={t("Rounds on record")} value={summary.total} />
        <Stat label={t("Completed")} value={summary.completed} />
        <Stat
          label={t("Rounds with failures")}
          value={summary.withFailures}
          colour={summary.withFailures ? "var(--amber)" : undefined}
        />
        <Stat label={t("Failed items")} value={summary.failures} colour={summary.failures ? "var(--red)" : undefined} />
        <Stat
          label={t("Mean score, completed")}
          value={summary.meanScore == null ? "—" : `${summary.meanScore.toFixed(1)}%`}
          colour={summary.meanScore == null ? undefined : scoreColour(summary.meanScore)}
        />
      </div>

      <div className={s.filters}>
        <Select value={status ?? ""} onChange={(e) => set("status", e.target.value || null)} aria-label={t("Status")}>
          <option value="">{t("Any status")}</option>
          {INSPECTION_STATUSES.map((st) => (
            <option key={st} value={st}>
              {t(INSPECTION_STATUS_LABEL[st])}
            </option>
          ))}
        </Select>
        <Select
          value={template ?? ""}
          onChange={(e) => set("template", e.target.value || null)}
          aria-label={t("Checklist")}
        >
          <option value="">{t("Any checklist")}</option>
          {templates.map(([key, name]) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </Select>
        <span className={s.count}>{rounds.data ? tn(rows.length, "{n} round", "{n} rounds") : ""}</span>
      </div>

      {rounds.isPending ? (
        <Skeleton rows={6} height={18} />
      ) : rounds.isError ? (
        <ErrorState error={rounds.error} retry={() => void rounds.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState>
          {t("No inspections match.")}{" "}
          {(all.data?.length ?? 0) === 0 &&
            t("The platform lists none at {code} — rounds are started from the field client.", { code: site.code })}
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("When")}</th>
              <th>{t("Checklist")}</th>
              <th>{t("Zone")}</th>
              <th>{t("Status")}</th>
              <th>{t("Score")}</th>
              <th style={{ textAlign: "end" }}>{t("Answers")}</th>
              <th style={{ textAlign: "end" }}>{t("Failed")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Row key={r.id} r={r} onOpen={() => navigate(`/inspections/${r.id}`)} />
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

function Row({ r, onOpen }: { r: Inspection; onOpen: () => void }) {
  return (
    <tr
      className={tableRowLinkClass}
      onClick={onOpen}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <td style={{ whiteSpace: "nowrap" }}>
        <Link to={`/inspections/${r.id}`} onClick={(e) => e.stopPropagation()}>
          {formatDateTime(inspectionWhen(r))}
        </Link>
      </td>
      <td className={s.tmpl}>
        <div>
          {r.templateName}
          {r.capturedOffline && (
            <span className={s.offline} title={t("Captured on the field client without signal and synced later")}>
              {t("OFFLINE")}
            </span>
          )}
        </div>
        <div className={s.tmplSub}>
          {r.template}
          {r.depth && ` · ${r.depth}`}
          {r.domain && ` · ${r.domain}`}
        </div>
      </td>
      <td className="mono">{r.zoneCode ?? "—"}</td>
      <td>
        <StatusPill status={r.status} />
      </td>
      <td>
        <ScoreCell score={r.score} coverage={r.coveragePct} status={r.status} />
      </td>
      <td className={numClass}>{r.answers}</td>
      <td className={`${numClass} ${r.failures ? s.failCount : s.zero}`}>{r.failures}</td>
    </tr>
  );
}

export function StatusPill({ status }: { status: InspectionStatus }) {
  const colour: Record<InspectionStatus, string> = {
    scheduled: "var(--dim)",
    in_progress: "var(--amber)",
    completed: "var(--green)",
    cancelled: "var(--dim)",
  };
  return (
    <span className={s.result} style={{ color: colour[status], borderColor: colour[status] }}>
      {t(INSPECTION_STATUS_LABEL[status])}
    </span>
  );
}

export function scoreColour(score: number): string {
  return score >= 95 ? "var(--go)" : score >= 85 ? "var(--caution)" : "var(--nogo)";
}

export function ScoreCell({
  score,
  coverage,
  status,
}: {
  score?: number;
  coverage?: number;
  status: InspectionStatus;
}) {
  if (status !== "completed") {
    return coverage != null ? (
      <span className="muted" style={{ fontSize: 13 }}>
        <span className={s.bar}>
          <span className={s.barFill} style={{ width: `${coverage}%`, "--score-c": "var(--blue)" } as CSSProperties} />
        </span>
        {t("{pct}% covered", { pct: coverage })}
      </span>
    ) : (
      <span className="muted">—</span>
    );
  }
  if (score == null) return <span className="muted">{t("not scored")}</span>;
  return (
    <span className={s.score} style={{ "--score-c": scoreColour(score) } as CSSProperties}>
      <span className={s.bar}>
        <span className={s.barFill} style={{ width: `${score}%` }} />
      </span>
      {score.toFixed(score % 1 ? 1 : 0)}%
    </span>
  );
}

function Stat({ label, value, colour }: { label: string; value: number | string; colour?: string }) {
  return (
    <div className={s.stat}>
      <div className={s.statValue} style={{ "--stat-c": colour } as CSSProperties}>
        {value}
      </div>
      <div className={s.statLabel}>{label}</div>
    </div>
  );
}
